import { notification } from '@todesktop/client-core';

import { clientLogger } from '@/lib/client-logger';
import { parsePrV2Route } from '@/lib/pr-v2/route-utils';
import { buildRealtimePullRequestId } from '@/lib/realtime/types';

type ToastRef = Awaited<ReturnType<typeof notification.create>>;

/**
 * A desktop toast about a pull request, tracked from before it shows so taking it back cannot
 * miss it.
 */
export type PullRequestToast = {
  readonly pullRequest: string;
  /** Set once the toast shows. */
  ref: ToastRef | null;
  /**
   * Its pull request was opened, merged or closed, or acted on, so the toast must not show or stay
   * up.
   */
  outdated: boolean;
};

/**
 * Shown toasts older than this many have long left the screen, so a long-running window forgets
 * them.
 */
const MAX_SHOWN_TOASTS = 50;

/** The desktop toasts this window is showing or showed, oldest first. */
let trackedToasts: PullRequestToast[] = [];

function closeToast(ref: ToastRef): void {
  notification.close({ ref }).catch((error: unknown) => {
    clientLogger.error('Failed to close a desktop notification', error);
  });
}

function pullRequestKey(path: string): string | null {
  const pr = parsePrV2Route(path.replace(/[?#].*$/, ''));
  return pr && buildRealtimePullRequestId(pr.owner, pr.repo, pr.number);
}

/**
 * Starts tracking a desktop toast whose link points at a pull request, before it is prepared and
 * shown, so taking back that pull request's toasts in the meantime marks it out of date.
 */
export function trackToast(link: string): PullRequestToast | null {
  const pullRequest = pullRequestKey(link);
  if (!pullRequest) {
    return null;
  }
  const toast: PullRequestToast = { pullRequest, ref: null, outdated: false };
  trackedToasts = [...trackedToasts, toast];
  return toast;
}

/**
 * Remembers a shown toast so it can be taken back, or takes it back at once if it went out of date
 * while showing.
 */
export function rememberToast(toast: PullRequestToast, ref: ToastRef): void {
  if (toast.outdated) {
    closeToast(ref);
    return;
  }
  toast.ref = ref;
  const shown = trackedToasts.filter((tracked) => tracked.ref);
  const forgotten = new Set(shown.slice(0, -MAX_SHOWN_TOASTS));
  trackedToasts = trackedToasts.filter((tracked) => !forgotten.has(tracked));
}

/** Stops tracking a toast that failed to show. */
export function forgetToast(toast: PullRequestToast): void {
  trackedToasts = trackedToasts.filter((tracked) => tracked !== toast);
}

/**
 * Takes back the toasts about the pull request at `path` once they are out of date: it was opened
 * in the app, merged or closed, or acted on from a toast.
 */
export function closeToastsAbout(path: string): void {
  const pullRequest = pullRequestKey(path);
  if (!pullRequest) {
    return;
  }
  for (const toast of trackedToasts) {
    if (toast.pullRequest === pullRequest) {
      toast.outdated = true;
      if (toast.ref) {
        closeToast(toast.ref);
      }
    }
  }
  trackedToasts = trackedToasts.filter((toast) => toast.pullRequest !== pullRequest);
}

/** Test helper for isolating the tracked toasts between tests. */
export function resetTrackedToastsForTests(): void {
  trackedToasts = [];
}
